import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { Paper } from '@material-ui/core'
import { useTableStyles } from './styles'
import Tabs from 'components/Shared/Tabs/Tabs'
import TableTab from './TableTab/TableTab'
import {
  clearLocalStorageByParentKey,
  getColumnsVisibilityFromLocalStorage,
  getSelectedTabFromLocalStorage,
  getSortFromLocalStorage,
  getTabsDefinitionsFromLocalStorage,
  saveColumnsVisibilityInLocalStorage,
  saveFiltersInLocalStorage,
  savePageInLocalStorage,
  saveRowsPerPageInLocalStorage,
  saveSelectedTabInLocalStorage,
  saveSortInLocalStorage,
  saveTabsDefinitionsInLocalStorage
} from './preferences'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'
import { tableAndTableTabCommonPropTypes, tablePropTypes } from './types'
import queryString from 'query-string'
import { intersection } from 'lodash'

function createTab (name, id = uuid()) {
  return ({ name, id })
}

const TableComponent = ({ tableKey, disableTabs, ...rest }, ref) => {
  const classes = useTableStyles()
  const { t } = useTranslation()

  const [tabsDefinitions, setTabsDefinitions] = useState(getTabsDefinitionsFromLocalStorage(tableKey, [createTab(t('table.tabs.newTabName'))]))
  const [selectedTab, setSelectedTab] = useState(getSelectedTabFromLocalStorage(tableKey))
  const [pages, setPages] = useState([])
  const [highlightedIds, setHighlightedIds] = useState([])
  // queryParamsLock to prevent TableTab updating query params before TableComponent
  const [queryParamsLock, setQueryParamsLock] = useState(true)

  useEffect(() => {
    saveTabsDefinitionsInLocalStorage(tableKey, tabsDefinitions)
  }, [tableKey, tabsDefinitions])

  const handleSelectedTabChange = useCallback((tabIndex) => {
    setSelectedTab(tabIndex)
    saveSelectedTabInLocalStorage(tableKey, tabIndex)
  }, [tableKey])

  const buildTabKey = useCallback((id) => `${tableKey}.${id}`, [tableKey])

  const addNewTab = useCallback((id = uuid()) => {
    setTabsDefinitions(oldTabs => [...oldTabs, createTab(t('table.tabs.newTabName'), id)])
    if (tabsDefinitions.length > selectedTab) {
      const previousTabKey = buildTabKey(tabsDefinitions[selectedTab].id)
      const newTabKey = buildTabKey(id)
      saveSortInLocalStorage(newTabKey, getSortFromLocalStorage(previousTabKey, []))
      saveColumnsVisibilityInLocalStorage(newTabKey, getColumnsVisibilityFromLocalStorage(previousTabKey, []))
    }
    handleSelectedTabChange(tabsDefinitions.length)
  }, [t, selectedTab, tabsDefinitions, buildTabKey, handleSelectedTabChange])

  // when other user shares his tab with set filters etc.
  useEffect(() => {
    const { tabId, filters, sort, rowsPerPage, page } = queryString.parse(window.location.search)
    if (!sort || !rowsPerPage || !page) {
      //  page without query params
    } else if (!tabsDefinitions.some(tab => tab.id === tabId) || !tabId) {
      const newTabId = uuid()
      const tabKey = buildTabKey(newTabId)
      saveFiltersInLocalStorage(tabKey, filters ? JSON.parse(filters) : [])
      saveSortInLocalStorage(tabKey, JSON.parse(sort))
      saveRowsPerPageInLocalStorage(tabKey, JSON.parse(rowsPerPage))
      savePageInLocalStorage(tabKey, JSON.parse(page))
      addNewTab(newTabId)
    }
    setQueryParamsLock(false)
  }, [tabsDefinitions, addNewTab, buildTabKey])

  const removeTab = useCallback((tabId) => {
    setTabsDefinitions(oldTabs => [...oldTabs.filter(tab => tab.id !== tabId)])
    clearLocalStorageByParentKey(buildTabKey(tabId))
  }, [buildTabKey])

  const changeTabName = useCallback((tabId, newName) => {
    setTabsDefinitions(oldTabs => {
      const newTabs = [...oldTabs]
      const updatedTabIndex = newTabs.findIndex(tab => tab.id === tabId)
      newTabs[updatedTabIndex] = createTab(newName)
      return newTabs
    })
  }, [])

  const handlePageResponseChange = useCallback(tabId => newPageResponse => {
    setPages(oldPages => {
      const newPages = [...oldPages
        .filter(page => page.tabId !== tabId)]
      newPages.push({ tabId, page: newPageResponse })
      return newPages
    })
  }, [])

  const renderTab = useCallback((tabId) => {
    if (queryParamsLock) {
      return null
    }
    const tabKey = buildTabKey(tabId)
    const pageResponse = pages.find(page => page.tabId === tabId)?.page
    return <TableTab
      {...rest}
      key={tabKey}
      tabKey={tabKey}
      tabId={tabId}
      pageResponse={pageResponse}
      onPageResponseChange={handlePageResponseChange(tabId)}
      onHighlightedIdsChange={setHighlightedIds}
      tableRef={ref}
      highlightedIds={highlightedIds}
    />
  }, [pages, rest, buildTabKey, highlightedIds, queryParamsLock, handlePageResponseChange, ref])

  const tabs = useMemo(() => [...tabsDefinitions.map(tab => {
    const idsOnPage = pages.find(page => page.tabId === tab.id)?.page?.items?.map(item => item.id)
    return ({
      label: tab.name,
      id: tab.id,
      isUpdated: intersection(highlightedIds, idsOnPage).length > 0,
      render: () => renderTab(tab.id)
    })
  })], [tabsDefinitions, renderTab, highlightedIds, pages])

  function renderContent () {
    return disableTabs ?
      tabs[0]?.render() :
      <Tabs
        id={'table-tabs'}
        className={classes.tabs}
        tabs={tabs}
        selectedTab={selectedTab}
        addNewTab={{
          label: '+',
          onClick: () => addNewTab()
        }}
        onSelectedTabChange={handleSelectedTabChange}
        onTabRemove={removeTab}
        onTabNameChange={changeTabName}>
      </Tabs>
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        {renderContent()}
      </Paper>
    </div>
  )
}

const ForwardedTableComponent = forwardRef(TableComponent)

ForwardedTableComponent.displayName = 'Table'
ForwardedTableComponent.propTypes = {
  ...tableAndTableTabCommonPropTypes,
  ...tablePropTypes
}

export default ForwardedTableComponent
